import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, User, UserPlus } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";

export default function Settings() {
  const [settings, setSettings] = useState({ academy_name: "", academy_logo_url: "", season: "", teams: [], age_groups: [] });
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "coach", player_ids: [] });
  const [players, setPlayers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editIds, setEditIds] = useState([]);

  async function reload() {
    const [s, u, pl] = await Promise.all([
      api.get("/settings"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/players").catch(() => ({ data: [] })),
    ]);
    setSettings({ ...s.data, teams: s.data.teams || [], age_groups: s.data.age_groups || [] });
    setUsers(u.data);
    setPlayers(pl.data);
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
      window.dispatchEvent(new CustomEvent("wahdat:settings-updated"));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function createUser(e) {
    e.preventDefault();
    try {
      const payload = { name: newUser.name, email: newUser.email, password: newUser.password, role: newUser.role };
      if (newUser.role === "parent") payload.player_ids = newUser.player_ids;
      await api.post("/users", payload);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "coach", player_ids: [] });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function updateUserPlayers() {
    if (!editingUser) return;
    try {
      await api.put(`/users/${editingUser.id}`, { player_ids: editIds });
      toast.success("Linked players updated");
      setEditingUser(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function togglePlayer(id, ids, setIds) {
    setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  }

  async function deleteUser(id) {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`); reload();
  }

  const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="overline">Admin</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Settings</h1>
      </div>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-4">
        <div className="overline">Academy</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label>
            <div className="text-xs text-neutral-500 font-semibold mb-1">Academy Name</div>
            <input className={inputCls} value={settings.academy_name || ""} onChange={(e) => setSettings({ ...settings, academy_name: e.target.value })} data-testid="setting-name" />
          </label>
          <label>
            <div className="text-xs text-neutral-500 font-semibold mb-1">Season</div>
            <input className={inputCls} value={settings.season || ""} onChange={(e) => setSettings({ ...settings, season: e.target.value })} />
          </label>
          <label className="md:col-span-2">
            <div className="text-xs text-neutral-500 font-semibold mb-2">Academy Logo</div>
            <ImageUploader
              value={settings.academy_logo_url}
              onChange={(url) => setSettings({ ...settings, academy_logo_url: url })}
              kind="logo"
              shape="square"
              size={96}
              testId="academy-logo-uploader"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ListEditor
            label="Teams"
            items={settings.teams}
            onChange={(v) => setSettings({ ...settings, teams: v })}
          />
          <div data-testid="settings-age-groups-auto" className="text-sm text-neutral-400">
            <div className="text-xs text-neutral-500 font-semibold mb-2">Age Groups</div>
            Assigned automatically from each player's date of birth (U6–U19, by birth year for the current season).
          </div>
        </div>

        <button onClick={save} data-testid="save-settings-btn" className="h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2">
          <FloppyDisk size={18} weight="bold" /> Save
        </button>
      </div>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-4">
        <div className="overline">Users</div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md">
              <div className="w-10 h-10 rounded-md bg-neutral-900 border border-neutral-800 flex items-center justify-center"><User size={18} weight="bold" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{u.name} <span className="overline ml-2">{u.role}</span></div>
                <div className="text-xs text-neutral-500">{u.email}</div>
                {u.role === "parent" && (
                  <div className="text-xs text-neutral-500 mt-1">
                    {u.player_ids?.length
                      ? `Linked: ${u.player_ids.map((id) => {
                          const p = players.find((x) => x.id === id);
                          return p ? `${p.first_name} ${p.last_name}` : "?";
                        }).join(", ")}`
                      : "No players linked yet"}
                  </div>
                )}
              </div>
              {u.role === "parent" && (
                <button
                  onClick={() => { setEditingUser(u); setEditIds(u.player_ids || []); }}
                  data-testid={`link-players-${u.id}`}
                  className="h-10 px-3 rounded-md border border-neutral-800 hover:border-[#06D6A0] hover:text-[#06D6A0] text-xs font-bold uppercase tracking-wider"
                >
                  Link Players
                </button>
              )}
              <button onClick={() => deleteUser(u.id)} className="text-neutral-500 hover:text-[#D90429]"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>

        <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
          <input required placeholder="Name" className={inputCls} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
          <input required type="email" placeholder="Email" className={inputCls} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
          <input required type="password" placeholder="Password" className={inputCls} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
          <select className={inputCls} value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="new-user-role">
            <option value="coach">Coach</option>
            <option value="admin">Admin</option>
            <option value="parent">Parent</option>
          </select>
          {newUser.role === "parent" && players.length > 0 && (
            <div className="md:col-span-4 bg-[#0a0a0a] border border-neutral-800 rounded-md p-3">
              <div className="overline mb-2">Link players (optional — can set later)</div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-auto">
                {players.map((p) => {
                  const on = newUser.player_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlayer(p.id, newUser.player_ids, (ids) => setNewUser({ ...newUser, player_ids: ids }))}
                      data-testid={`new-user-player-${p.id}`}
                      className={`text-xs px-3 py-1.5 rounded-md border font-semibold ${on ? "bg-[#06D6A0] text-[#050505] border-transparent" : "bg-[#121212] border-neutral-800 text-neutral-400 hover:border-[#06D6A0]"}`}
                    >
                      {p.first_name} {p.last_name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {newUser.role === "parent" && players.length === 0 && (
            <div className="md:col-span-4 bg-[#0a0a0a] border border-neutral-800 rounded-md p-3 text-xs text-neutral-400">
              Add players first, then edit this parent to link them.
            </div>
          )}
          <button type="submit" data-testid="create-user-btn" className="md:col-span-4 h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2">
            <UserPlus size={18} weight="bold" /> Create User
          </button>
        </form>
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="overline">Link players to</div>
              <div className="font-display font-black text-xl uppercase">{editingUser.name}</div>
            </div>
            <div className="flex flex-wrap gap-2 max-h-64 overflow-auto">
              {players.map((p) => {
                const on = editIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlayer(p.id, editIds, setEditIds)}
                    data-testid={`edit-link-${p.id}`}
                    className={`text-xs px-3 py-1.5 rounded-md border font-semibold ${on ? "bg-[#06D6A0] text-[#050505] border-transparent" : "bg-[#0a0a0a] border-neutral-800 text-neutral-400 hover:border-[#06D6A0]"}`}
                  >
                    {p.first_name} {p.last_name}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditingUser(null)} className="flex-1 h-12 rounded-md border border-neutral-800 hover:bg-neutral-900 font-bold text-sm uppercase tracking-wider">Cancel</button>
              <button onClick={updateUserPlayers} data-testid="save-links-btn" className="flex-1 h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-bold text-sm uppercase tracking-wider">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListEditor({ label, items, onChange }) {
  const [val, setVal] = useState("");
  const list = items || [];
  return (
    <div>
      <div className="text-xs text-neutral-500 font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0a0a0a] border border-neutral-800 text-xs">
            {it}
            <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-[#D90429]">
              <Trash size={12} weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val.trim()) { onChange([...list, val.trim()]); setVal(""); } } }}
          placeholder={`Add ${label.toLowerCase()}…`}
          className="flex-1 h-10 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429] text-sm"
        />
        <button
          type="button"
          onClick={() => { if (val.trim()) { onChange([...list, val.trim()]); setVal(""); } }}
          className="h-10 px-3 rounded-md border border-neutral-800 hover:border-[#06D6A0] hover:text-[#06D6A0]"
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
